import { createMobxStoreProvider } from "src/common/factories/createMobxStoreProvider.tsx";
import { action, computed, makeObservable, observable } from "mobx";
import { replacePathParams } from "@exonn/utils";
import { type Router } from "@remix-run/router";
import { matchRoutes } from "react-router-dom";
import { TabsStore } from "src/lib/tabs-ui/TabsStore.tsx";

export class ModuleTabsStore {
  @observable
  paths: string[];
  editPath: string | undefined;
  createPath: string | undefined;
  baseStore: TabsStore;
  router: Router;
  constructor({
    paths,
    editPath,
    createPath,
    router,
  }: {
    paths: string[];
    editPath?: string;
    createPath?: string;

    router: Router;
  }) {
    this.paths = paths;
    this.editPath = editPath;
    this.createPath = createPath;
    this.router = router;
    this.baseStore = new TabsStore();
    makeObservable(this);
  }
  @action.bound
  setPaths(paths: string[] | { (paths: string[]): string[] }) {
    this.paths = typeof paths === "function" ? paths(this.paths) : paths;
  }

  @action.bound
  closeTab(path: string) {
    this.setPaths(
      this.paths.filter((innerPath) => !innerPath.startsWith(path)),
    );
  }

  @action.bound
  closeEditTab(params: Record<string, any>) {
    if (this.editPath) {
      this.closeTab(replacePathParams(this.editPath, params));
    }
  }
  @action.bound
  closeCreateTab(params: Record<string, any> = {}) {
    if (this.createPath) {
      this.closeTab(replacePathParams(this.createPath, params));
    }
  }

  @computed
  get isCreateTabOpen() {
    if (!this.createPath) {
      return false;
    }
    return (this.paths || []).some((path) => {
      const matches = matchRoutes(this.router.routes, path);

      return (matches || []).some(
        (match) => match.route.path === this.createPath,
      );
    });
  }

  openCreateTab() {
    if (!this.createPath) {
      throw new Error(
        "can't open 'create' tab. module doesn't have 'create' tab",
      );
    }
    const createPath = (this.paths || []).find((path) => {
      const matches = matchRoutes(this.router.routes, path);

      return (matches || []).some(
        (match) => match.route.path === this.createPath,
      );
    });
    return this.router.navigate(createPath || this.createPath);
  }

  @computed
  get editParams(): any[] {
    if (!this.editPath) {
      return [];
    }
    return this.paths
      .map((path) => {
        const matches = matchRoutes(this.router.routes, path) || [];

        for (let i = 0; i < matches.length; i++) {
          if (matches[i].route.path === this.editPath) {
            return matches[i].params;
          }
        }

        return {};
      })
      .filter((params) => Object.keys(params).length > 0);
  }

  @computed
  get editPaths(): string[] {
    return this.paths.filter((path) => {
      if (!this.editPath) {
        return false;
      }
      const matches = matchRoutes(this.router.routes, path) || [];

      for (let i = 0; i < matches.length; i++) {
        if (matches[i].route.path === this.editPath) {
          return true;
        }
      }
      return false;
    });
  }

  @computed
  get createPaths(): string[] {
    return this.paths.filter((path) => {
      if (!this.createPath) {
        return false;
      }
      const matches = matchRoutes(this.router.routes, path) || [];

      for (let i = 0; i < matches.length; i++) {
        if (matches[i].route.path === this.createPath) {
          return true;
        }
      }
      return false;
    });
  }
}

export const { Provider: ModuleTabsProvider, useStore: useModuleTabs } =
  createMobxStoreProvider<ModuleTabsStore>("ModuleTabsStore");
